// Zirkuläre Winkelarithmetik für Heading/Bearing-Vergleiche.
//
// Warum nicht einfach `Math.abs(a - b)`?
//   Weil Winkel modulo 360 wrappen: 359° und 1° sind nur 2° auseinander,
//   nicht 358°. Ein naiver Vergleich versagt genau an der Nord-Grenze und
//   der Bug tritt dort auf, wo viele Fahrer tatsächlich fahren. Ein Mini-
//   Jitter-Filter wie `if (|a-b| < 1) skip;` wird nahe Norden wirkungslos:
//   jeder Mikro-Wobble über 0° schlägt als "358° Sprung" durch und triggert
//   einen teuren Kartenbearing-Update.
//
// Diese Datei kapselt die korrekte Circular-Math so, dass das Problem an
// genau einer Stelle gelöst ist und nicht in jedem Callsite neu.

/**
 * Kürzeste vorzeichenbehaftete Winkeldifferenz a - b in Grad, Range (-180, 180].
 *
 * Beispiele:
 *   angleDiff(10, 350)  =>   20  (im Uhrzeigersinn 20°)
 *   angleDiff(350, 10)  =>  -20  (gegen Uhrzeigersinn 20°)
 *   angleDiff( 90,  0)  =>   90
 *   angleDiff(  0, 90)  =>  -90
 *
 * Die Implementation `((a - b + 540) % 360) - 180` funktioniert, weil:
 *   - `+540` stellt sicher, dass der Ausdruck vor dem `%` positiv ist
 *     (JavaScripts `%` liefert bei negativen Dividenden negative Ergebnisse).
 *   - `% 360` klemmt auf [0, 360).
 *   - `- 180` verschiebt auf [-180, 180).
 */
export function angleDiff(a: number, b: number): number {
  return ((a - b + 540) % 360) - 180;
}

/**
 * Absolute kürzeste Winkeldistanz zwischen zwei Headings, Range [0, 180].
 * Wrappt korrekt um 0°/360° herum.
 *
 * Beispiele:
 *   angleDelta(359,   1)  => 2
 *   angleDelta(  1, 359)  => 2
 *   angleDelta(179, 181)  => 2
 *   angleDelta(  0, 180)  => 180
 *   angleDelta( 90,  95)  => 5
 */
export function angleDelta(a: number, b: number): number {
  return Math.abs(angleDiff(a, b));
}
